import React, { useEffect, useRef, useState } from 'react';
import { motion, AnimatePresence, useScroll, useTransform } from "framer-motion";
import { useNavigate, Link } from 'react-router-dom';
import { gsap } from "gsap";
import { ScrollTrigger } from "gsap/ScrollTrigger";
import { ChevronLeft, ChevronRight } from 'lucide-react';
import { courses } from '../data/courses';

if (typeof window !== "undefined") {
  gsap.registerPlugin(ScrollTrigger);
}

const Hero = () => {
  const navigate = useNavigate();
  const [currentSlide, setCurrentSlide] = useState(0);
  const { scrollYProgress } = useScroll();
  const y = useTransform(scrollYProgress, [0, 1], [0, 200]);
  const sectionRef = useRef(null);
  const titleRef = useRef(null);
  const descRef = useRef(null);
  const buttonRef = useRef(null);

  const banners = [
    '/banner1.png',
    '/banner2.jpg',
    '/banners3.jpg',
    '/banner4.avif'
  ];

  useEffect(() => {
    const interval = setInterval(() => {
      setCurrentSlide((prev) => (prev + 1) % banners.length);
    }, 5000);
    return () => clearInterval(interval);
  }, [banners.length]);

  useEffect(() => {
    const ctx = gsap.context(() => {
      gsap.fromTo(titleRef.current, { opacity: 0, y: 100 }, { opacity: 1, y: 0, duration: 1.5, ease: "power2.out" });
      gsap.fromTo(descRef.current, { opacity: 0, y: 50 }, { opacity: 1, y: 0, duration: 1, delay: 0.5, ease: "power2.out" });
      gsap.fromTo(buttonRef.current, { opacity: 0, y: 30 }, { opacity: 1, y: 0, duration: 1, delay: 0.8, ease: "power2.out" });
    }, sectionRef);

    return () => ctx.revert();
  }, []);

  const nextSlide = () => setCurrentSlide((prev) => (prev + 1) % banners.length);
  const prevSlide = () => setCurrentSlide((prev) => (prev - 1 + banners.length) % banners.length);
  const goToSlide = (index) => setCurrentSlide(index);

  return (
    <>
      {/* GREEN WRAPPER */}
      <div className="bg-[#0b9949]">
        <div className="h-[130px] md:h-[175px] lg:h-[120px]"></div>

        {/* MARQUEE */}
        <div className="relative overflow-hidden">
          <div className="absolute left-0 top-0 bottom-0 w-16 md:w-32 bg-gradient-to-r from-[#0b9949] to-transparent z-10"></div>
          <div className="absolute right-0 top-0 bottom-0 w-16 md:w-32 bg-gradient-to-l from-[#0b9949] to-transparent z-10"></div>

          <div className="py-3 md:py-4">
            <div className="flex animate-scroll">
              {[...courses, ...courses, ...courses].map((course, index) => (
                <Link
                  to={`/course-details/${course.id}`}
                  key={`marquee-${index}`}
                  className="flex-shrink-0 mx-2 md:mx-4 group"
                >
                  <div className="flex items-center bg-white/10 backdrop-blur-sm rounded-full px-4 md:px-6 py-2 border border-white/20 hover:bg-white/25 hover:scale-105 transition-all duration-300 cursor-pointer">
                    <span className="w-2 h-2 bg-yellow-400 rounded-full mr-2 md:mr-3 group-hover:animate-pulse"></span>
                    <span className="text-white font-medium text-xs md:text-sm whitespace-nowrap group-hover:text-yellow-200 transition-colors">
                      {course.title}
                    </span>
                    <svg className="w-3 h-3 md:w-4 md:h-4 ml-2 text-white/60 group-hover:text-yellow-400 transition-all"
                      fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9 5l7 7-7 7" />
                    </svg>
                  </div>
                </Link>
              ))}
            </div>
          </div>
        </div>
      </div>

      {/* HERO SECTION */}
      <section ref={sectionRef} className="h-screen w-full flex items-center justify-center relative overflow-hidden">

        {/* BACKGROUND WITH PARALLAX */}
        <motion.div className="absolute inset-0" style={{ y }}>
          <AnimatePresence mode="wait">
            <motion.div
              key={currentSlide}
              initial={{ opacity: 0, scale: 1.1 }}
              animate={{ opacity: 1, scale: 1 }}
              exit={{ opacity: 0, scale: 0.95 }}
              transition={{ duration: 1, ease: "easeInOut" }}
              className="absolute inset-0"
              style={{
                backgroundImage: `url('${banners[currentSlide]}')`,
                backgroundSize: "cover",
                backgroundPosition: "center",
              }}
            />
          </AnimatePresence>
        </motion.div>

        {/* SOFT OVERLAY */}
        <div className="absolute inset-0 bg-black/25"></div>

        {/* SLIDER ARROWS */}
        <button onClick={prevSlide} className="absolute left-4 md:left-8 top-1/2 -translate-y-1/2 z-20 bg-white/20 hover:bg-white/30 backdrop-blur-sm text-white p-3 rounded-full transition-all duration-300">
          <ChevronLeft size={24} />
        </button>
        <button onClick={nextSlide} className="absolute right-4 md:right-8 top-1/2 -translate-y-1/2 z-20 bg-white/20 hover:bg-white/30 backdrop-blur-sm text-white p-3 rounded-full transition-all duration-300">
          <ChevronRight size={24} />
        </button>

        {/* SLIDE DOTS */}
        <div className="absolute bottom-8 left-1/2 -translate-x-1/2 z-20 flex gap-3">
          {banners.map((_, index) => (
            <button
              key={index}
              onClick={() => goToSlide(index)}
              className={`transition-all duration-300 rounded-full ${currentSlide === index ? 'w-12 h-3 bg-[#0b9949]' : 'w-3 h-3 bg-white/50'}`}
            />
          ))}
        </div>

        {/* HERO TEXT */}
        <motion.div className="relative z-30 text-center px-6 max-w-5xl mx-auto"
          initial={{ opacity: 0, y: 60 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 1, ease: "easeOut" }}
        >
          {/* BRIGHT HEADING */}
          <h1
            ref={titleRef}
            className="text-3xl sm:text-4xl md:text-5xl lg:text-6xl font-extrabold text-white mb-6 leading-tight drop-shadow-[0_6px_20px_rgba(255,255,255,0.25)]"
          >
            Low Levy (WTC) Certificate of Competency
            <br />
            <span className="text-xl sm:text-2xl md:text-4xl lg:text-5xl drop-shadow-[0_6px_20px_rgba(255,255,255,0.25)]">
              Course (CoC)
            </span>
          </h1>

          {/* BRIGHT SUBTEXT */}
          <p
            ref={descRef}
            className="max-w-2xl mx-auto text-white/95 text-base sm:text-lg md:text-xl mb-8 drop-shadow-[0_4px_15px_rgba(0,0,0,0.9)] font-medium leading-relaxed"
          >
           Join Greenmark to improve your skills and gain industry-recognized certifications.
          </p>

          {/* CTA BUTTON */}
          <motion.button
            ref={buttonRef}
            onClick={() => navigate('/courses')}
            whileHover={{ scale: 1.07, boxShadow: "0 12px 35px rgba(11,153,73,0.6)" }}
            whileTap={{ scale: 0.95 }}
            className="bg-[#0b9949] text-white px-8 sm:px-10 py-3 sm:py-4 text-base sm:text-lg font-semibold rounded-full shadow-[0_6px_25px_rgba(0,0,0,0.4)] hover:shadow-[0_10px_45px_rgba(11,153,73,0.55)] hover:bg-[#0da255] transition-all"
          >
            Explore Courses
          </motion.button>
        </motion.div>
      </section>

      <style jsx>{`
        @keyframes scroll {
          0% { transform: translateX(0); }
          100% { transform: translateX(-33.33%); }
        }
        .animate-scroll {
          animation: scroll 30s linear infinite;
          width: max-content;
        }
        .animate-scroll:hover {
          animation-play-state: paused;
        }
      `}</style>
    </>
  );
};

export default Hero;
